/**
 * Player inventory + ship equipment slots (Space Rangers style).
 */
import { INVENTORY_SLOTS, SHIP_SLOTS } from '../constants.js';
import { starterLoadout, damageItem } from './items.js';

export class Inventory {
    constructor() {
        this.slots = new Array(INVENTORY_SLOTS).fill(null);
        this.equipped = {};
        for (const s of SHIP_SLOTS) this.equipped[s] = null;
        this.fuel = 40;
        this.maxFuel = 100;
        this.cargoBonus = 0;
    }

    get capacity() {
        return INVENTORY_SLOTS + this.cargoBonus;
    }

    /** Free bag slots (including cargo bonus expansion of array) */
    ensureSize() {
        while (this.slots.length < this.capacity) this.slots.push(null);
    }

    countFree() {
        this.ensureSize();
        return this.slots.filter(s => !s).length;
    }

    add(item) {
        this.ensureSize();
        // Stack stackables
        if (item.stackable) {
            for (let i = 0; i < this.slots.length; i++) {
                const s = this.slots[i];
                if (s && s.stackable && s.name === item.name && s.rarity === item.rarity) {
                    s.qty = (s.qty || 1) + (item.qty || 1);
                    return true;
                }
            }
        }
        const idx = this.slots.findIndex(s => !s);
        if (idx === -1) return false;
        this.slots[idx] = item;
        return true;
    }

    removeAt(index) {
        if (index < 0 || index >= this.slots.length) return null;
        const item = this.slots[index];
        this.slots[index] = null;
        return item;
    }

    findIndex(itemId) {
        return this.slots.findIndex(s => s && s.id === itemId);
    }

    equip(bagIndex) {
        const item = this.slots[bagIndex];
        if (!item || item.kind !== 'module' || !item.slot) return false;
        const slot = item.slot;
        // Unequip existing
        if (this.equipped[slot]) {
            if (!this.add(this.equipped[slot])) return false; // bag full
        }
        this.equipped[slot] = item;
        this.slots[bagIndex] = null;
        this._recalcCargo();
        return true;
    }

    unequip(slot) {
        const item = this.equipped[slot];
        if (!item) return false;
        if (!this.add(item)) return false;
        this.equipped[slot] = null;
        this._recalcCargo();
        return true;
    }

    _recalcCargo() {
        let bonus = 0;
        for (const s of Object.values(this.equipped)) {
            if (s?.stats?.cargo) bonus += s.stats.cargo;
        }
        this.cargoBonus = bonus;
        this.ensureSize();
    }

    /** Aggregate stats from all equipped modules (broken parts give 0) */
    getEquipmentStats() {
        const total = {
            damage: 0, fireRate: 0, range: 0, maxHp: 0,
            speed: 0, magnet: 0, shield: 0, armor: 0, cargo: 0,
        };
        for (const item of Object.values(this.equipped)) {
            if (!item?.stats || item.broken) continue;
            const mult = item.maxDurability
                ? Math.max(0.15, (item.durability ?? 100) / item.maxDurability)
                : 1;
            for (const [k, v] of Object.entries(item.stats)) {
                if (total[k] !== undefined) total[k] += Number(v) * mult;
            }
        }
        // Round for display stability
        for (const k of Object.keys(total)) {
            total[k] = +Number(total[k]).toFixed(2);
        }
        return total;
    }

    /** Damage a random equipped module (combat wear) */
    wearRandomPart(amount = 12) {
        const parts = Object.entries(this.equipped).filter(([, it]) => it && !it.broken);
        if (!parts.length) return null;
        const [slot, item] = parts[Math.floor(Math.random() * parts.length)];
        const broke = damageItem(item, amount);
        return { slot, item, broke };
    }

    listBroken() {
        return Object.entries(this.equipped)
            .filter(([, it]) => it?.broken)
            .map(([slot, item]) => ({ slot, item }));
    }

    addFuel(n) {
        this.fuel = Math.min(this.maxFuel, this.fuel + n);
    }

    spendFuel(n) {
        if (this.fuel < n) return false;
        this.fuel -= n;
        return true;
    }

    /** Seed starter gear once */
    giveStarter() {
        for (const item of starterLoadout()) {
            if (item.kind === 'module' && item.slot && !this.equipped[item.slot]) {
                this.equipped[item.slot] = item;
            } else {
                this.add(item);
            }
            if (item.shape === 'fuel') this.addFuel(item.stats?.fuel || 15);
        }
        this._recalcCargo();
    }

    serialize() {
        return {
            slots: this.slots,
            equipped: this.equipped,
            fuel: this.fuel,
            maxFuel: this.maxFuel,
        };
    }
}

/**
 * Apply equipment stats onto the live player object.
 */
export function applyLoadoutToPlayer(player, inventory) {
    if (!player || !inventory) return;
    const s = inventory.getEquipmentStats();
    const baseSpeed = player.baseSpeed || 3;
    const baseDamage = player.baseDamage || 10;
    const baseMaxHp = 100;
    const baseCooldown = 20;

    player.maxHp = baseMaxHp + (s.maxHp || 0) + (s.shield || 0);
    player.hp = Math.min(player.hp, player.maxHp);
    player.damage = baseDamage + (s.damage || 0);
    player.speed = baseSpeed + (s.speed || 0);
    player.baseSpeed = player.speed;
    player.baseDamage = player.damage;
    player.magnetRange = (player.baseMagnetRange || 0) + (s.magnet || 0);
    // fireRate: reduce cooldown
    const fr = Math.max(0, Math.min(0.7, s.fireRate || 0));
    player.autoShootMaxCooldown = Math.max(6, Math.round(baseCooldown * (1 - fr)));
    // range stored on player for shooting
    player.shootRangeBonus = s.range || 0;
    player.armor = s.armor || 0;
}
